import { lazy, type ReactNode, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { SAML_CALLBACK_PATH } from "../auth/samlAuth";
import { RouteErrorPage } from "../components/common/RouteErrorPage";
import { RouteFallback } from "../components/common/RouteFallback";
import { Layout } from "../components/layout/Layout";
import { AdminModuleKey } from "../features/auth/adminModules";
import { Dashboard } from "../pages/Dashboard/Dashboard";
import { AuthCallback } from "../pages/Login/AuthCallback";
import { Login } from "../pages/Login/Login";
import { UnauthorizedPage } from "../pages/Unauthorized/UnauthorizedPage";
import { ProtectedRoute } from "./ProtectedRoute";

const EventManagement = lazy(() =>
	import("../pages/EventManagement/EventManagement").then((m) => ({
		default: m.EventManagement,
	})),
);
const EventFormPage = lazy(() =>
	import("../pages/EventManagement/EventFormPage").then((m) => ({
		default: m.EventFormPage,
	})),
);
const EventDetailPage = lazy(() =>
	import("../pages/EventManagement/EventDetailPage").then((m) => ({
		default: m.EventDetailPage,
	})),
);
const CabanaBookingList = lazy(() =>
	import("../pages/CabanaBooking").then((m) => ({
		default: m.CabanaBookingList,
	})),
);
const CabanaBookingFormPage = lazy(() =>
	import("../pages/CabanaBooking").then((m) => ({
		default: m.CabanaBookingFormPage,
	})),
);
const CabanaBookingDetailPage = lazy(() =>
	import("../pages/CabanaBooking").then((m) => ({
		default: m.CabanaBookingDetailPage,
	})),
);
const RestaurantBookingList = lazy(() =>
	import("../pages/RestaurantBooking").then((m) => ({
		default: m.RestaurantBookingList,
	})),
);
const RestaurantBookingFormPage = lazy(() =>
	import("../pages/RestaurantBooking").then((m) => ({
		default: m.RestaurantBookingFormPage,
	})),
);
const RestaurantBookingDetailPage = lazy(() =>
	import("../pages/RestaurantBooking").then((m) => ({
		default: m.RestaurantBookingDetailPage,
	})),
);
const PropertyManagement = lazy(() =>
	import("../pages/PropertyManagement/PropertyManagement").then((m) => ({
		default: m.PropertyManagement,
	})),
);
const PropertyDetailPage = lazy(() =>
	import("../pages/PropertyManagement/PropertyDetailPage").then((m) => ({
		default: m.PropertyDetailPage,
	})),
);
const PropertyUnitDetailPage = lazy(() =>
	import("../pages/PropertyManagement/PropertyUnitDetailPage").then((m) => ({
		default: m.PropertyUnitDetailPage,
	})),
);
const PropertyMarketing = lazy(() =>
	import("../pages/PropertyMarketing/PropertyMarketing").then((m) => ({
		default: m.PropertyMarketing,
	})),
);
const CreatePropertyListingPage = lazy(() =>
	import("../pages/PropertyMarketing/CreatePropertyListingPage").then((m) => ({
		default: m.CreatePropertyListingPage,
	})),
);
const CampaignDetailPage = lazy(() =>
	import("../pages/PropertyMarketing/CampaignDetailPage").then((m) => ({
		default: m.CampaignDetailPage,
	})),
);
const UserManagement = lazy(() =>
	import("../pages/UserManagement/UserManagement").then((m) => ({
		default: m.UserManagement,
	})),
);
const UserDetailsPage = lazy(() =>
	import("../pages/UserDetails/UserDetails").then((m) => ({
		default: m.UserDetailsPage,
	})),
);
const UserPreStayDetailPage = lazy(() =>
	import("../pages/UserDetails/UserPreStayDetailPage").then((m) => ({
		default: m.UserPreStayDetailPage,
	})),
);
const UserEventBookingDetailPage = lazy(() =>
	import("../pages/UserDetails/UserEventBookingDetailPage").then((m) => ({
		default: m.UserEventBookingDetailPage,
	})),
);
const UserVenueReservationDetailPage = lazy(() =>
	import("../pages/UserDetails/UserVenueReservationDetailPage").then((m) => ({
		default: m.UserVenueReservationDetailPage,
	})),
);
const SubAdminManagement = lazy(() =>
	import("../pages/SubAdminManagement/SubAdminManagement").then((m) => ({
		default: m.SubAdminManagement,
	})),
);
const SubAdminFormPage = lazy(() =>
	import("../pages/SubAdminManagement/SubAdminFormPage").then((m) => ({
		default: m.SubAdminFormPage,
	})),
);
const SubAdminDetailPage = lazy(() =>
	import("../pages/SubAdminManagement/SubAdminDetailPage").then((m) => ({
		default: m.SubAdminDetailPage,
	})),
);
const RolesAndPermissions = lazy(() =>
	import("../pages/RolesAndPermissions/RolesAndPermissions").then((m) => ({
		default: m.RolesAndPermissions,
	})),
);
const RoleDetailPage = lazy(() =>
	import("../pages/RolesAndPermissions/RoleDetailPage").then((m) => ({
		default: m.RoleDetailPage,
	})),
);
const RoleFormPage = lazy(() =>
	import("../pages/RolesAndPermissions/RoleFormPage").then((m) => ({
		default: m.RoleFormPage,
	})),
);
const BeneficiaryApprovalsList = lazy(() =>
	import("../pages/BeneficiaryApprovals").then((m) => ({
		default: m.BeneficiaryApprovalsList,
	})),
);
const BeneficiaryApprovalDetailPage = lazy(() =>
	import("../pages/BeneficiaryApprovals").then((m) => ({
		default: m.BeneficiaryApprovalDetailPage,
	})),
);
const AppUpdatePage = lazy(() =>
	import("../pages/AppUpdate").then((m) => ({
		default: m.AppUpdatePage,
	})),
);
const StaticContentListPage = lazy(() =>
	import("../pages/StaticContent").then((m) => ({
		default: m.StaticContentListPage,
	})),
);
const StaticContentDocumentEditPage = lazy(() =>
	import("../pages/StaticContent").then((m) => ({
		default: m.StaticContentDocumentEditPage,
	})),
);
const RevenueManagementListPage = lazy(() =>
	import("../pages/RevenueManagement").then((m) => ({
		default: m.RevenueManagementListPage,
	})),
);
const RevenuePropertyPeriodsPage = lazy(() =>
	import("../pages/RevenueManagement").then((m) => ({
		default: m.RevenuePropertyPeriodsPage,
	})),
);
const RevenuePeriodDetailPage = lazy(() =>
	import("../pages/RevenueManagement").then((m) => ({
		default: m.RevenuePeriodDetailPage,
	})),
);
const RevenueUnitDetailPage = lazy(() =>
	import("../pages/RevenueManagement").then((m) => ({
		default: m.RevenueUnitDetailPage,
	})),
);
const ChargesManagementListPage = lazy(() =>
	import("../pages/ChargesManagement").then((m) => ({
		default: m.ChargesManagementListPage,
	})),
);
const ChargesPropertyPeriodsPage = lazy(() =>
	import("../pages/ChargesManagement").then((m) => ({
		default: m.ChargesPropertyPeriodsPage,
	})),
);
const ChargesPeriodDetailPage = lazy(() =>
	import("../pages/ChargesManagement").then((m) => ({
		default: m.ChargesPeriodDetailPage,
	})),
);
const ChargesUnitDetailPage = lazy(() =>
	import("../pages/ChargesManagement").then((m) => ({
		default: m.ChargesUnitDetailPage,
	})),
);

function LazyPage({ children }: { children: ReactNode }) {
	return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

const PlaceholderPage = ({ title }: { title: string }) => (
	<div className="p-6">
		<h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
		<p className="mt-2 text-gray-600">This page is coming soon.</p>
	</div>
);

export const router = createBrowserRouter([
	{
		path: "/login",
		element: <Login />,
		errorElement: <RouteErrorPage fullPage />,
	},
	{
		path: SAML_CALLBACK_PATH,
		element: <AuthCallback />,
		errorElement: <RouteErrorPage fullPage />,
	},
	{
		path: "/auth/callback",
		element: <AuthCallback />,
		errorElement: <RouteErrorPage fullPage />,
	},
	{
		path: "/",
		element: (
			<ProtectedRoute>
				<Layout />
			</ProtectedRoute>
		),
		errorElement: <RouteErrorPage fullPage />,
		children: [
			{
				errorElement: <RouteErrorPage />,
				children: [
					{
						index: true,
						element: <Navigate to="/dashboard" replace />,
					},
					{
						path: "dashboard",
						element: <Dashboard />,
					},
					{
						path: "users",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.USER_MANAGEMENT}>
								<LazyPage>
									<UserManagement />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "users/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.USER_MANAGEMENT}>
								<LazyPage>
									<UserDetailsPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "users/:id/pre-stay/:reservationId",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.USER_MANAGEMENT}>
								<LazyPage>
									<UserPreStayDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "users/:id/events/:bookingId",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.USER_MANAGEMENT}>
								<LazyPage>
									<UserEventBookingDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "users/:id/restaurant/:bookingId",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.USER_MANAGEMENT}>
								<LazyPage>
									<UserVenueReservationDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "users/:id/cabana/:bookingId",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.USER_MANAGEMENT}>
								<LazyPage>
									<UserVenueReservationDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "properties",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MANAGEMENT}>
								<LazyPage>
									<PropertyManagement />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "properties/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MANAGEMENT}>
								<LazyPage>
									<PropertyDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "properties/:id/units/:unitId",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MANAGEMENT}>
								<LazyPage>
									<PropertyUnitDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "properties/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MANAGEMENT}>
								<PlaceholderPage title="Edit Property" />
							</ProtectedRoute>
						),
					},
					{
						path: "property-marketing",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MARKETING}>
								<LazyPage>
									<PropertyMarketing />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "property-marketing/create",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MARKETING}>
								<LazyPage>
									<CreatePropertyListingPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "property-marketing/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MARKETING}>
								<LazyPage>
									<CampaignDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "property-marketing/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.PROPERTY_MARKETING}>
								<LazyPage>
									<CreatePropertyListingPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "events",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.EVENT_MANAGEMENT}>
								<LazyPage>
									<EventManagement />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "events/create",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.EVENT_MANAGEMENT}>
								<LazyPage>
									<EventFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "events/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.EVENT_MANAGEMENT}>
								<LazyPage>
									<EventDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "events/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.EVENT_MANAGEMENT}>
								<LazyPage>
									<EventFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "cabana-booking/create",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.CABANA_MANAGEMENT}>
								<LazyPage>
									<CabanaBookingFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "cabana-booking/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.CABANA_MANAGEMENT}>
								<LazyPage>
									<CabanaBookingFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "cabana-booking/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.CABANA_MANAGEMENT}>
								<LazyPage>
									<CabanaBookingDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "cabana-booking",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.CABANA_MANAGEMENT}>
								<LazyPage>
									<CabanaBookingList />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "restaurant-booking/create",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.FNB_MANAGEMENT}>
								<LazyPage>
									<RestaurantBookingFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "restaurant-booking/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.FNB_MANAGEMENT}>
								<LazyPage>
									<RestaurantBookingFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "restaurant-booking/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.FNB_MANAGEMENT}>
								<LazyPage>
									<RestaurantBookingDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "restaurant-booking",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.FNB_MANAGEMENT}>
								<LazyPage>
									<RestaurantBookingList />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "approvals/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.BENEFICIARY_APPROVALS}>
								<LazyPage>
									<BeneficiaryApprovalDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "approvals",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.BENEFICIARY_APPROVALS}>
								<LazyPage>
									<BeneficiaryApprovalsList />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "sub-admin",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.KERZNER_SUB_ADMIN}>
								<LazyPage>
									<SubAdminManagement />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "sub-admin/add",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.KERZNER_SUB_ADMIN}>
								<LazyPage>
									<SubAdminFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "sub-admin/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.KERZNER_SUB_ADMIN}>
								<LazyPage>
									<SubAdminDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "sub-admin/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.KERZNER_SUB_ADMIN}>
								<LazyPage>
									<SubAdminFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "roles-permissions",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.ROLES_AND_PERMISSIONS}>
								<LazyPage>
									<RolesAndPermissions />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "roles-permissions/create",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.ROLES_AND_PERMISSIONS}>
								<LazyPage>
									<RoleFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "roles-permissions/:id/edit",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.ROLES_AND_PERMISSIONS}>
								<LazyPage>
									<RoleFormPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "roles-permissions/:id",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.ROLES_AND_PERMISSIONS}>
								<LazyPage>
									<RoleDetailPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "app-update",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.APP_UPDATE}>
								<LazyPage>
									<AppUpdatePage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "revenue-management/properties/:propertyId/periods/:periodId/units/:unitId",
						element: (
							<LazyPage>
								<RevenueUnitDetailPage />
							</LazyPage>
						),
					},
					{
						path: "revenue-management/properties/:propertyId/periods/:periodId",
						element: (
							<LazyPage>
								<RevenuePeriodDetailPage />
							</LazyPage>
						),
					},
					{
						path: "revenue-management/properties/:propertyId",
						element: (
							<LazyPage>
								<RevenuePropertyPeriodsPage />
							</LazyPage>
						),
					},
					{
						path: "revenue-management",
						element: (
							<LazyPage>
								<RevenueManagementListPage />
							</LazyPage>
						),
					},
					{
						path: "charges-management/properties/:propertyId/periods/:periodId/units/:unitId",
						element: (
							<LazyPage>
								<ChargesUnitDetailPage />
							</LazyPage>
						),
					},
					{
						path: "charges-management/properties/:propertyId/periods/:periodId",
						element: (
							<LazyPage>
								<ChargesPeriodDetailPage />
							</LazyPage>
						),
					},
					{
						path: "charges-management/properties/:propertyId",
						element: (
							<LazyPage>
								<ChargesPropertyPeriodsPage />
							</LazyPage>
						),
					},
					{
						path: "charges-management",
						element: (
							<LazyPage>
								<ChargesManagementListPage />
							</LazyPage>
						),
					},
					{
						path: "static-content",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.STATIC_CONTENT}>
								<LazyPage>
									<StaticContentListPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "static-content/:slug",
						element: (
							<ProtectedRoute moduleKey={AdminModuleKey.STATIC_CONTENT}>
								<LazyPage>
									<StaticContentDocumentEditPage />
								</LazyPage>
							</ProtectedRoute>
						),
					},
					{
						path: "settings",
						element: <PlaceholderPage title="Settings" />,
					},
					{
						path: "profile",
						element: <PlaceholderPage title="Profile" />,
					},
					{
						path: "unauthorized",
						element: <UnauthorizedPage />,
					},
				],
			},
		],
	},
]);
